import { useEffect, useState } from "react";
import type { EditorFeatureAttrs } from "../editorTypes";

interface FeatureFormProps {
  attrs: EditorFeatureAttrs;
  onSave: (propriedades: Record<string, unknown>) => void;
}

function TextField({
  label,
  value,
  onCommit,
  placeholder,
}: {
  label: string;
  value: string;
  onCommit: (value: string) => void;
  placeholder?: string;
}) {
  const [local, setLocal] = useState(value);

  useEffect(() => setLocal(value), [value]);

  return (
    <div>
      <label className="text-[11px] text-foreground-500 block mb-1">{label}</label>
      <input
        value={local}
        placeholder={placeholder}
        onChange={(e) => {
          setLocal(e.target.value);
          onCommit(e.target.value);
        }}
        className="w-full text-xs px-2 py-1.5 border border-background-200 rounded-md"
      />
    </div>
  );
}

function SelectField({
  label,
  value,
  options,
  onCommit,
}: {
  label: string;
  value: string;
  options: string[];
  onCommit: (value: string) => void;
}) {
  return (
    <div>
      <label className="text-[11px] text-foreground-500 block mb-1">{label}</label>
      <select
        value={value}
        onChange={(e) => onCommit(e.target.value)}
        className="w-full text-xs px-2 py-1.5 border border-background-200 rounded-md"
      >
        <option value="">—</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    </div>
  );
}

function CheckboxField({ label, checked, onCommit }: { label: string; checked: boolean; onCommit: (value: boolean) => void }) {
  return (
    <label className="flex items-center gap-2 text-xs text-foreground-700 cursor-pointer">
      <input type="checkbox" checked={checked} onChange={(e) => onCommit(e.target.checked)} />
      {label}
    </label>
  );
}

function TextAreaField({
  label,
  value,
  onCommit,
}: {
  label: string;
  value: string;
  onCommit: (value: string) => void;
}) {
  const [local, setLocal] = useState(value);
  useEffect(() => setLocal(value), [value]);

  return (
    <div>
      <label className="text-[11px] text-foreground-500 block mb-1">{label}</label>
      <textarea
        value={local}
        onChange={(e) => {
          setLocal(e.target.value);
          onCommit(e.target.value);
        }}
        rows={3}
        className="w-full text-xs px-2 py-1.5 border border-background-200 rounded-md resize-none"
      />
    </div>
  );
}

export default function FeatureForm({ attrs, onSave }: FeatureFormProps) {
  const props = attrs.propriedades;

  const commit = (patch: Record<string, unknown>) => {
    onSave({ ...props, ...patch });
  };

  const stringOf = (key: string) => (typeof props[key] === "string" ? (props[key] as string) : "");
  const boolOf = (key: string) => Boolean(props[key]);

  return (
    <div className="space-y-3">
      <TextAreaField label="Observação" value={stringOf("observacao")} onCommit={(v) => commit({ observacao: v })} />

      {attrs.tipoObjeto === "ARVORE" && (
        <>
          <TextField label="Código" value={stringOf("codigo")} onCommit={(v) => commit({ codigo: v })} placeholder="ex.: ARV-001" />
          <TextField
            label="Espécie estimada"
            value={stringOf("especie_estimada")}
            onCommit={(v) => commit({ especie_estimada: v })}
            placeholder="ex.: Ipê Amarelo (estimado)"
          />
          <SelectField
            label="Porte visual"
            value={stringOf("porte_visual")}
            options={["PEQUENO", "MEDIO", "GRANDE"]}
            onCommit={(v) => commit({ porte_visual: v })}
          />
          <SelectField
            label="Estado aparente"
            value={stringOf("estado_aparente")}
            options={["BOM", "REGULAR", "RUIM", "MORTA"]}
            onCommit={(v) => commit({ estado_aparente: v })}
          />
          <CheckboxField
            label="Conflito com fiação"
            checked={boolOf("conflito_fiacao")}
            onCommit={(v) => commit({ conflito_fiacao: v })}
          />
          <CheckboxField
            label="Conflito com calçada"
            checked={boolOf("conflito_calcada")}
            onCommit={(v) => commit({ conflito_calcada: v })}
          />
        </>
      )}

      {attrs.tipoObjeto === "COPA_ARVORE" && (
        <>
          <TextField
            label="Árvore vinculada (código)"
            value={stringOf("arvore_vinculada")}
            onCommit={(v) => commit({ arvore_vinculada: v })}
          />
          <SelectField
            label="Tipo de copa"
            value={stringOf("tipo_copa")}
            options={["DENSA", "ESPARSA", "IRREGULAR"]}
            onCommit={(v) => commit({ tipo_copa: v })}
          />
        </>
      )}

      <TextField label="Tags (separadas por vírgula)" value={stringOf("tags")} onCommit={(v) => commit({ tags: v })} />
    </div>
  );
}
